import { faDollarCircle, faEllipsisV, faEnvelopeOpenDollar, faEye, faTrash } from "@fortawesome/pro-solid-svg-icons"
import { faMoneyCheckDollar } from "@fortawesome/pro-duotone-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Dropdown, Modal } from "antd"
import { useState } from "react"
import { ItemType } from "antd/es/menu/interface"
import { StatementSummary } from "../../../domain/invoicing/statement"
import { useDeleteStatementMutation, useMarkStatementNotPaidManuallyMutation, useStatementQuery } from "../../../domain/invoicing/statementsQueries"
import StatementPaymentModal from "../PayInvoice/StatementPaymentModal"
import SendStatementModal from "../SendInvoice/SendStatementModal"
import EmailCustomerStatementModal from "../SendInvoice/EmailCustomerStatementModal"
import { canViewStatementReceipt, markNotPaidConfirmation, statementActionAffordances } from "./statementActionsModel"

type Props = {
  statement: StatementSummary
}

const StatementActions = ({ statement }: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [hasBeenOpened, setHasBeenOpened] = useState(false)
  const [showPayment, setShowPayment] = useState(false)
  const [showSend, setShowSend] = useState(false)
  const [showReminder, setShowReminder] = useState(false)
  const deleteMutation = useDeleteStatementMutation()
  const notPaidMutation = useMarkStatementNotPaidManuallyMutation()

  const affordances = statementActionAffordances(statement)

  const detailQuery = useStatementQuery(statement.statementId, {
    disabled: !hasBeenOpened || !affordances.isPaid,
  })
  const showViewReceipt = canViewStatementReceipt(statement, detailQuery.data?.paymentPageUrl)

  const items: ItemType[] = [
    affordances.canSend ? {
      key: 'send-statement',
      icon: <FontAwesomeIcon icon={faEnvelopeOpenDollar} />,
      label: affordances.sendLabel,
      onClick: () => {
        if (affordances.sendIsReminder) {
          setShowReminder(true)
        } else {
          setShowSend(true)
        }
      }
    } : null,
    affordances.canPay ? {
      key: 'pay-statement',
      icon: <FontAwesomeIcon icon={faMoneyCheckDollar} />,
      label: 'Pay Statement',
      onClick: () => setShowPayment(true)
    } : null,
    showViewReceipt ? {
      key: 'view-receipt',
      icon: <FontAwesomeIcon icon={faEye} />,
      label: 'View Receipt',
      onClick: () => window.open(detailQuery.data!.paymentPageUrl, '_blank')
    } : null,
    affordances.canMarkNotPaid ? {
      key: 'mark-not-paid',
      icon: <FontAwesomeIcon icon={faDollarCircle} />,
      label: 'Mark Statement Not Paid',
      disabled: notPaidMutation.isLoading,
      onClick: () => {
        const confirmation = markNotPaidConfirmation(statement)
        Modal.confirm({
          title: confirmation.title,
          content: confirmation.description,
          okText: 'Yes',
          cancelText: 'No',
          onOk: () => notPaidMutation.mutate(statement.statementId)
        })
      }
    } : null,
    (affordances.canSend || affordances.canPay || showViewReceipt || affordances.canMarkNotPaid) ? { type: 'divider' as const } : null,
    {
      key: 'delete-statement',
      icon: <FontAwesomeIcon icon={faTrash} />,
      label: 'Delete Statement',
      danger: true,
      disabled: !affordances.canDelete || deleteMutation.isLoading,
      onClick: () => {
        Modal.confirm({
          title: `Delete statement ${statement.statementNumber}?`,
          content: `This deletes the statement and its ${statement.invoiceCount} invoices.`,
          okText: 'Delete',
          okButtonProps: { danger: true },
          onOk: () => deleteMutation.mutate({ statementId: statement.statementId, deleteFromQuickBooks: false })
        })
      }
    }
  ].filter(Boolean) as ItemType[]

  return <>
    <Dropdown
      trigger={['click']}
      open={isOpen}
      onOpenChange={open => {
        setIsOpen(open)
        if (open) {
          setHasBeenOpened(true)
        }
      }}
      menu={{ items }}
    >
      <Button
        type="text"
        icon={<FontAwesomeIcon icon={faEllipsisV} />}
        onClick={e => e.stopPropagation()}
      />
    </Dropdown>
    <StatementPaymentModal
      isOpen={showPayment}
      onClose={() => setShowPayment(false)}
      statementId={statement.statementId}
      customerId={statement.customerId}
      statementTotal={statement.total}
    />
    {showSend && <SendStatementModal statement={statement} onClose={() => setShowSend(false)} />}
    <EmailCustomerStatementModal
      isOpen={showReminder}
      onClose={() => setShowReminder(false)}
      statement={statement}
    />
  </>
}

export default StatementActions
